import { useRef } from "react";
import { isInFeishu } from "src/common/client-platform";
import useSearchs from "./use-searchs";

export type SuccessCallback = (code: string) => void;

const FEISHU_CLIENT_AUTO_LOGIN_FLAG = "feishuclientautologin";
const FEISHU_H5_JSSDK_SCRIPT_ID = "feishu-h5-jssdk-script";
const FEISHU_H5_JSSDK_SRC =
  "https://lf-scm-cn.feishucdn.com/lark/op/h5-js-sdk-1.5.44.js";

export { isInFeishu } from "src/common/client-platform";

export function buildFeishuClientAutoLoginRedirectUri(href: string) {
  const url = new URL(href);
  url.searchParams.set(FEISHU_CLIENT_AUTO_LOGIN_FLAG, "1");
  return url.toString();
}

function ensureFeishuH5Sdk() {
  if (window.h5sdk?.ready && window.tt) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      FEISHU_H5_JSSDK_SCRIPT_ID
    ) as HTMLScriptElement | null;

    const handleLoad = () => {
      if (window.h5sdk?.ready && window.tt) {
        resolve();
        return;
      }

      reject(new Error("Feishu H5 JSSDK is unavailable"));
    };
    const handleError = () => {
      reject(new Error("Failed to load Feishu H5 JSSDK"));
    };

    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        handleLoad();
        return;
      }

      existingScript.addEventListener("load", handleLoad, { once: true });
      existingScript.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = FEISHU_H5_JSSDK_SCRIPT_ID;
    script.src = FEISHU_H5_JSSDK_SRC;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      handleLoad();
    };
    script.onerror = handleError;
    document.head.appendChild(script);
  });
}

function useFeishuClientAutoLogin(feishuAppId: string, state: string) {
  const inFeishuClient = isInFeishu();
  const { code, feishuclientautologin } = useSearchs<{
    code?: string;
    feishuclientautologin?: string;
  }>();
  const successCallbackRef = useRef<SuccessCallback | null>(null);

  function onSuccess(cb: SuccessCallback) {
    successCallbackRef.current = cb;
  }

  function tryFeishuClientAutoLogin() {
    if (!inFeishuClient || !feishuAppId) {
      return;
    }

    if (code && feishuclientautologin) {
      successCallbackRef.current?.(code);
      return;
    }

    const requestAuthCode = () => {
      window.tt?.requestAuthCode?.({
        appId: feishuAppId,
        success: ({ code: authCode }) => {
          successCallbackRef.current?.(authCode);
        },
      });
    };

    ensureFeishuH5Sdk()
      .then(() => {
        window.h5sdk?.ready(() => {
          if (window.tt?.requestAccess) {
            window.tt.requestAccess({
              appID: feishuAppId,
              scopeList: [],
              state,
              success: ({ code: authCode }) => {
                successCallbackRef.current?.(authCode);
              },
              fail: (error) => {
                if (error?.errno === 103) {
                  requestAuthCode();
                }
              },
            });
            return;
          }

          requestAuthCode();
        });
      })
      .catch(() => {
        // Keep the QR login page available when the in-client SDK is unavailable.
      });
  }

  return {
    onSuccess,
    tryFeishuClientAutoLogin,
  };
}

export default useFeishuClientAutoLogin;
